/**
 * ConfluenceDashboard — Phase B Signal Confluence Monitor
 * =========================================================
 * SESSION 137: Real-time display of:
 * - Active pattern setups (state machines in progress)
 * - Emitted composite signals (High Probability Setups)
 * - Level test tracker (repeated bounce counts)
 * - LCP sweep events
 * - Intraday stacked imbalance events
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Layers, TrendingUp, TrendingDown, Target, Activity,
  Zap, Shield, Clock, RefreshCw, ChevronRight, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PATTERN_LABELS = {
  ABSORPTION_EXHAUSTION: 'Absorption → Exhaustion',
  LCP_SWEEP: 'LCP Sweep',
  DHOL_CONTINUATION: 'DHOL Continuation',
  VA_REJECTION: 'Value Area Rejection',
  SLINGSHOT_EP: 'Slingshot + EP Zone',
};

const PATTERN_COLORS = {
  ABSORPTION_EXHAUSTION: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  LCP_SWEEP: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  DHOL_CONTINUATION: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  VA_REJECTION: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  SLINGSHOT_EP: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
};

const STATE_COLORS = {
  STATE_1: 'text-yellow-400',
  STATE_2: 'text-amber-400',
  STATE_3: 'text-green-400',
  EMITTED: 'text-cyan-400',
  EXPIRED: 'text-gray-500',
  CANCELLED: 'text-red-400',
};

const ScoreBadge = ({ score }) => {
  const color = score >= 80 ? 'text-green-400 bg-green-500/10 border-green-500/30'
    : score >= 65 ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
    : 'text-gray-400 bg-gray-500/10 border-gray-500/30';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${color}`} data-testid="confluence-score-badge">
      {score}
    </span>
  );
};

const ConfluenceDashboard = () => {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [signals, setSignals] = useState({ NIFTY: [], BANKNIFTY: [] });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, signalsRes] = await Promise.all([
        authFetch(`${API}/api/advanced-signals/confluence/status`),
        authFetch(`${API}/api/advanced-signals/confluence/signals`),
      ]);

      if (statusRes.ok) {
        const d = await statusRes.json();
        setData(d.data);
      }
      if (signalsRes.ok) {
        const s = await signalsRes.json();
        setSignals(s.signals || { NIFTY: [], BANKNIFTY: [] });
      }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Confluence fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const renderSymbolPanel = (symbol) => {
    const symbolData = data?.[symbol];
    const symbolSignals = signals[symbol] || [];
    const orchestrator = symbolData?.orchestrator || {};
    const levelTracker = symbolData?.level_tracker || {};
    const stackedTracker = symbolData?.stacked_tracker || {};
    const lcpTagger = symbolData?.lcp_tagger || {};
    const eventBuffer = symbolData?.event_buffer || {};
    const activeSetups = orchestrator.setups || [];
    const isNifty = symbol === 'NIFTY';
    const accent = isNifty ? 'cyan' : 'amber';

    return (
      <div key={symbol} className="space-y-3" data-testid={`confluence-panel-${symbol.toLowerCase()}`}>
        {/* Symbol Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full bg-${accent}-400 animate-pulse`} />
            <h3 className={`text-sm font-bold text-${accent}-400`}>{symbol}</h3>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-500">
            <span>Setups: <span className="text-white font-mono">{activeSetups.length}</span></span>
            <span>Signals: <span className="text-white font-mono">{symbolSignals.length}</span></span>
            <span>Events: <span className="text-white font-mono">{eventBuffer.total_events || 0}</span></span>
          </div>
        </div>

        {/* Emitted Composite Signals */}
        {symbolSignals.length > 0 && (
          <div className="space-y-2">
            {symbolSignals.map((sig, idx) => {
              const pColor = PATTERN_COLORS[sig.pattern] || PATTERN_COLORS.ABSORPTION_EXHAUSTION;
              const isLong = sig.direction === 'LONG';
              return (
                <div
                  key={idx}
                  className={`rounded-lg border-2 ${isLong ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'} p-3`}
                  data-testid={`composite-signal-${symbol.toLowerCase()}-${idx}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className={`w-4 h-4 ${isLong ? 'text-green-400' : 'text-red-400'}`} />
                      <span className="text-xs font-bold text-white">High Probability Setup</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${pColor.bg} ${pColor.text} ${pColor.border} border font-medium`}>
                        {PATTERN_LABELS[sig.pattern] || sig.pattern}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ScoreBadge score={sig.confluence_score} />
                      <span className={`text-xs font-bold ${isLong ? 'text-green-400' : 'text-red-400'}`}>
                        {isLong ? <TrendingUp className="w-3.5 h-3.5 inline" /> : <TrendingDown className="w-3.5 h-3.5 inline" />}
                        {' '}{sig.direction}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-black/20 rounded p-1.5">
                      <div className="text-[8px] text-gray-500 uppercase">Entry</div>
                      <div className="text-xs font-bold font-mono text-white">{sig.entry_price?.toFixed(2)}</div>
                    </div>
                    <div className="bg-black/20 rounded p-1.5">
                      <div className="text-[8px] text-gray-500 uppercase">SL</div>
                      <div className="text-xs font-bold font-mono text-red-400">{sig.stop_loss?.toFixed(2)}</div>
                    </div>
                    <div className="bg-black/20 rounded p-1.5">
                      <div className="text-[8px] text-gray-500 uppercase">T1</div>
                      <div className="text-xs font-bold font-mono text-green-400">{sig.target_1?.toFixed(2)}</div>
                    </div>
                    <div className="bg-black/20 rounded p-1.5">
                      <div className="text-[8px] text-gray-500 uppercase">R:R</div>
                      <div className="text-xs font-bold font-mono text-cyan-400">1:{sig.risk_reward}</div>
                    </div>
                  </div>

                  {/* Context row */}
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                    {sig.god_score_at_emit && <span>GS: <span className="text-white">{sig.god_score_at_emit}</span></span>}
                    {sig.day_type && <span>Day: <span className="text-white">{sig.day_type}</span></span>}
                    {sig.is_high_conviction && <span className="text-green-400 font-medium">HIGH CONVICTION</span>}
                    <span className="ml-auto"><Clock className="w-3 h-3 inline" /> {new Date(sig.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Active Setups (patterns in progress) */}
        {activeSetups.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Patterns Building</p>
            {activeSetups.map((setup, idx) => {
              const pColor = PATTERN_COLORS[setup.pattern] || PATTERN_COLORS.ABSORPTION_EXHAUSTION;
              const stateColor = STATE_COLORS[setup.state] || 'text-gray-400';
              return (
                <div key={idx} className={`rounded-lg border ${pColor.border} ${pColor.bg} px-3 py-2 flex items-center gap-3`} data-testid={`active-setup-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${pColor.text}`}>{PATTERN_LABELS[setup.pattern]}</span>
                      <ChevronRight className="w-3 h-3 text-gray-600" />
                      <span className={`text-xs font-bold ${setup.direction === 'LONG' ? 'text-green-400' : 'text-red-400'}`}>
                        {setup.direction}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500">
                      <span>Level: <span className="text-white font-mono">{setup.anchor_price?.toFixed(0)}</span></span>
                      {setup.level_tests > 0 && <span>Tests: <span className="text-amber-400">{setup.level_tests}x</span></span>}
                      <span>Age: {setup.age_min?.toFixed(0)}m</span>
                    </div>
                  </div>
                  {/* State progression dots */}
                  <div className="flex items-center gap-1">
                    {['STATE_1', 'STATE_2', 'STATE_3'].map(s => (
                      <div
                        key={s}
                        className={`w-2.5 h-2.5 rounded-full border ${
                          setup.state === s ? `${stateColor} border-current bg-current/30 animate-pulse` :
                          ['STATE_1', 'STATE_2', 'STATE_3'].indexOf(s) < ['STATE_1', 'STATE_2', 'STATE_3'].indexOf(setup.state)
                            ? 'bg-green-400/50 border-green-400/50'
                            : 'bg-gray-700 border-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-600 text-xs">
            <Activity className="w-5 h-5 mx-auto mb-1 opacity-50" />
            No patterns building
          </div>
        )}

        {/* Level Tests + LCPs + Stacked Imbalances — compact stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 p-2 text-center">
            <div className="text-[9px] text-gray-500 uppercase">Level Tests</div>
            <div className="text-sm font-bold text-amber-400">{levelTracker.significant_levels || 0}</div>
            <div className="text-[9px] text-gray-600">{levelTracker.total_tracked_levels || 0} tracked</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 p-2 text-center">
            <div className="text-[9px] text-gray-500 uppercase">LCP Sweeps</div>
            <div className="text-sm font-bold text-cyan-400">{lcpTagger.total_lcps_today || 0}</div>
            <div className="text-[9px] text-gray-600">today</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 p-2 text-center">
            <div className="text-[9px] text-gray-500 uppercase">Stacked Imb.</div>
            <div className="text-sm font-bold text-green-400">{stackedTracker.total_events_today || 0}</div>
            <div className="text-[9px] text-gray-600">
              B:{stackedTracker.recent_buy || 0} S:{stackedTracker.recent_sell || 0}
            </div>
          </div>
        </div>

        {/* Significant Levels Detail */}
        {(levelTracker.levels || []).length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Key Levels</p>
            {(levelTracker.levels || []).slice(0, 5).map((lvl, idx) => (
              <div key={idx} className="flex items-center justify-between px-3 py-1.5 rounded bg-gray-800/30 border border-gray-700/30">
                <span className="text-xs font-mono text-white">{lvl.anchor_price?.toFixed(2)}</span>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className={`font-bold ${lvl.test_count >= 5 ? 'text-red-400' : 'text-amber-400'}`}>
                    {lvl.test_count}x tested
                  </span>
                  {lvl.peak_test_count >= 5 && (
                    <span className="text-red-400 font-medium">HIGH</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading confluence data...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="confluence-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-amber-400" />
          <div>
            <h2 className="text-lg font-bold text-white">Signal Confluence</h2>
            <p className="text-[10px] text-gray-500">Pattern state machines — chaining signals into high-probability setups</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-[10px] text-gray-600">
              Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="p-1.5 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:bg-gray-700/50 transition-colors"
            data-testid="confluence-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
        <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-amber-400/80">
          Read-only overlay — existing signals work independently. Composite signals appear when multiple detectors confirm the same trade setup.
        </p>
      </div>

      {/* Two-column layout for NIFTY / BANKNIFTY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderSymbolPanel('NIFTY')}
        {renderSymbolPanel('BANKNIFTY')}
      </div>
    </div>
  );
};

export default ConfluenceDashboard;
